import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { catalogService, CatalogItem } from '../../services/catalog.service';
import { manualEntryService } from '../../services/manual-entry.service';
import { projectService, Project } from '../../services/project.service';
import { clientService, Client } from '../../services/client.service';
import { LoadingState } from '../../components/ui/LoadingState';

type TabType = 'income' | 'expense';

interface FormState {
  folio: string;
  entry_date: string;
  document_number: string;
  beneficiary_type: '' | 'office' | 'supplier' | 'client' | 'contractor';
  beneficiary_id: string;
  beneficiary_external_name: string;
  concept_id: string;
  project_id: string;
  details: string;
  movement_type_id: string;
  movement_detail_id: string;
  amount: number;
  affects_wallet: boolean;
  department_id: string;
  currency_type_id: string;
}

const EMPTY_FORM: FormState = {
  folio: '',
  entry_date: new Date().toISOString().slice(0, 10),
  document_number: '',
  beneficiary_type: '',
  beneficiary_id: '',
  beneficiary_external_name: '',
  concept_id: '',
  project_id: '',
  details: '',
  movement_type_id: '',
  movement_detail_id: '',
  amount: 0,
  affects_wallet: true,
  department_id: '',
  currency_type_id: 'ct-cash',
};

export function ManualCapturePage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  const [activeTab, setActiveTab] = useState<TabType>('income');
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const [concepts, setConcepts] = useState<CatalogItem[]>([]);
  const [movementTypes, setMovementTypes] = useState<CatalogItem[]>([]);
  const [movementDetails, setMovementDetails] = useState<CatalogItem[]>([]);
  const [departments, setDepartments] = useState<CatalogItem[]>([]);
  const [currencyTypes, setCurrencyTypes] = useState<CatalogItem[]>([]);
  const [suppliers, setSuppliers] = useState<CatalogItem[]>([]);
  const [contractors, setContractors] = useState<CatalogItem[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);

  const [entries, setEntries] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadCatalogs();
    loadProjects();
    loadClients();
  }, []);

  useEffect(() => {
    loadFilteredCatalogs();
    loadEntries();
    setForm((prev) => ({
      ...prev,
      concept_id: '',
      movement_type_id: '',
      movement_detail_id: '',
    }));
  }, [activeTab]);

  const loadCatalogs = async () => {
    try {
      const [deps, curr, sups, conts] = await Promise.all([
        catalogService.list('departments'),
        catalogService.list('currency_types'),
        catalogService.list('suppliers'),
        catalogService.list('contractors'),
      ]);
      setDepartments(deps);
      setCurrencyTypes(curr);
      setSuppliers(sups);
      setContractors(conts);
    } catch (err: any) {
      setError(err.message || 'Error al cargar catálogos');
    }
  };

  const loadFilteredCatalogs = async () => {
    try {
      const kind = activeTab === 'income' ? 'I' : 'E';
      const [conc, mTypes, mDetails] = await Promise.all([
        catalogService.list('concepts', { kind }),
        catalogService.list('movement_types', { kind }),
        catalogService.list('movement_details', { kind }),
      ]);
      setConcepts(conc);
      setMovementTypes(mTypes);
      setMovementDetails(mDetails);
    } catch (err: any) {
      setError(err.message || 'Error al cargar catálogos');
    }
  };

  const loadProjects = async () => {
    try {
      const data = await projectService.getProjects();
      setProjects(data.filter((p) => ['planning', 'in_progress', 'on_hold'].includes(p.status)));
    } catch (err) {
      console.error('Error al cargar proyectos:', err);
    }
  };

  const loadClients = async () => {
    try {
      const data = await clientService.getClients();
      setClients(data.filter((c) => c.status === 'active'));
    } catch (err) {
      console.error('Error al cargar clientes:', err);
    }
  };

  const loadEntries = async () => {
    setIsLoading(true);
    try {
      const data = await manualEntryService.list({ entry_type: activeTab });
      setEntries(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar registros');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof FormState, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    setSuccess('');

    if (!form.concept_id) {
      setError('Selecciona un concepto');
      setIsSaving(false);
      return;
    }
    if (!form.amount || form.amount <= 0) {
      setError('El importe debe ser mayor a 0');
      setIsSaving(false);
      return;
    }
    if (form.affects_wallet && !form.department_id) {
      setError('Si afecta wallet, debes seleccionar un departamento');
      setIsSaving(false);
      return;
    }
    if (form.beneficiary_type && form.beneficiary_id === '__OTHER__' && !form.beneficiary_external_name) {
      setError('Ingresa el nombre del beneficiario');
      setIsSaving(false);
      return;
    }

    try {
      const isExternal = form.beneficiary_id === '__OTHER__';
      const payload: any = {
        entry_type: activeTab,
        folio: form.folio || undefined,
        entry_date: new Date(form.entry_date).getTime(),
        document_number: form.document_number || undefined,
        beneficiary_type: form.beneficiary_type || undefined,
        beneficiary_id: isExternal ? undefined : form.beneficiary_id || undefined,
        beneficiary_external_name: isExternal ? form.beneficiary_external_name : undefined,
        concept_id: form.concept_id,
        project_id: form.project_id || undefined,
        details: form.details || undefined,
        movement_type_id: form.movement_type_id || undefined,
        movement_detail_id: form.movement_detail_id || undefined,
        amount: form.amount,
        affects_wallet: form.affects_wallet ? 1 : 0,
        department_id: form.department_id || undefined,
        currency_type_id: form.affects_wallet ? form.currency_type_id : undefined,
      };

      await manualEntryService.create(payload);
      setSuccess('Registro guardado correctamente');
      setForm({ ...EMPTY_FORM, entry_date: new Date().toISOString().slice(0, 10) });
      await loadEntries();
    } catch (err: any) {
      setError(err.message || 'Error al guardar registro');
    } finally {
      setIsSaving(false);
    }
  };

  const getBeneficiaryOptions = (): { value: string; label: string }[] => {
    let base: { value: string; label: string }[] = [];
    switch (form.beneficiary_type) {
      case 'client':
        base = clients.map((c) => ({ value: c.id, label: c.name }));
        break;
      case 'supplier':
        base = suppliers.map((s) => ({ value: s.id, label: s.name }));
        break;
      case 'contractor':
        base = contractors.map((c) => ({ value: c.id, label: c.name }));
        break;
      case 'office':
        base = departments.map((d) => ({ value: d.id, label: d.name }));
        break;
    }
    if (form.beneficiary_type === 'supplier' || form.beneficiary_type === 'contractor') {
      base.push({ value: '__OTHER__', label: '+ Otro (escribir nombre)' });
    }
    return base;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('es-MX');
  };

  const showExternalInput = form.beneficiary_id === '__OTHER__';

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Captura Manual</h1>
        <p className="text-xs text-slate-500">Registro manual de ingresos y egresos</p>
      </div>

      <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm">
        <button
          onClick={() => setActiveTab('income')}
          className={`flex-1 py-2 rounded-md text-xs font-semibold transition-colors ${
            activeTab === 'income' ? 'bg-primary text-white' : 'text-slate-600'
          }`}
        >
          📥 Ingresos
        </button>
        <button
          onClick={() => setActiveTab('expense')}
          className={`flex-1 py-2 rounded-md text-xs font-semibold transition-colors ${
            activeTab === 'expense' ? 'bg-primary text-white' : 'text-slate-600'
          }`}
        >
          📤 Egresos
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-3 py-2">
          {success}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-bold mb-3">
          {activeTab === 'income' ? 'Nuevo Ingreso' : 'Nuevo Egreso'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Folio</label>
              <input
                type="text"
                value={form.folio}
                onChange={(e) => handleChange('folio', e.target.value)}
                placeholder="Ej: ING-001"
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Fecha *</label>
              <input
                type="date"
                required
                value={form.entry_date}
                onChange={(e) => handleChange('entry_date', e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">No. Documento</label>
            <input
              type="text"
              value={form.document_number}
              onChange={(e) => handleChange('document_number', e.target.value)}
              placeholder="Número de documento"
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Tipo de Beneficiario</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {[
                { value: 'office', label: '🏢 Oficina' },
                { value: 'supplier', label: '🏭 Proveedor' },
                { value: 'client', label: '👤 Cliente' },
                { value: 'contractor', label: '🔨 Contratista' },
              ].map((opt) => (
                <label key={opt.value} className="flex items-center gap-1 text-xs">
                  <input
                    type="radio"
                    name="beneficiary_type"
                    value={opt.value}
                    checked={form.beneficiary_type === opt.value}
                    onChange={(e) => {
                      handleChange('beneficiary_type', e.target.value);
                      handleChange('beneficiary_id', '');
                      handleChange('beneficiary_external_name', '');
                    }}
                    className="accent-primary"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {form.beneficiary_type && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Beneficiario</label>
              <select
                value={form.beneficiary_id}
                onChange={(e) => {
                  handleChange('beneficiary_id', e.target.value);
                  if (e.target.value !== '__OTHER__') {
                    handleChange('beneficiary_external_name', '');
                  }
                }}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
              >
                <option value="">Seleccionar...</option>
                {getBeneficiaryOptions().map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {showExternalInput && (
                <input
                  type="text"
                  value={form.beneficiary_external_name}
                  onChange={(e) => handleChange('beneficiary_external_name', e.target.value)}
                  placeholder="Nombre del beneficiario"
                  className="w-full mt-2 px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                />
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Concepto *</label>
            <select
              required
              value={form.concept_id}
              onChange={(e) => handleChange('concept_id', e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            >
              <option value="">Seleccionar concepto...</option>
              {concepts.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Proyecto</label>
            <select
              value={form.project_id}
              onChange={(e) => handleChange('project_id', e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            >
              <option value="">Sin proyecto</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Detalles</label>
            <textarea
              rows={2}
              value={form.details}
              onChange={(e) => handleChange('details', e.target.value)}
              placeholder="Observaciones..."
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm resize-none focus:border-primary focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Tipo Movimiento</label>
              <select
                value={form.movement_type_id}
                onChange={(e) => handleChange('movement_type_id', e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
              >
                <option value="">Seleccionar...</option>
                {movementTypes.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Detalle Movimiento</label>
              <select
                value={form.movement_detail_id}
                onChange={(e) => handleChange('movement_detail_id', e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
              >
                <option value="">Seleccionar...</option>
                {movementDetails.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Importe *</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={form.amount || ''}
                onChange={(e) => handleChange('amount', parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Departamento</label>
              <select
                value={form.department_id}
                onChange={(e) => handleChange('department_id', e.target.value)}
                disabled={!form.affects_wallet}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none disabled:bg-slate-50"
              >
                <option value="">Seleccionar...</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={form.affects_wallet}
              onChange={(e) => handleChange('affects_wallet', e.target.checked)}
              className="w-4 h-4 accent-primary"
            />
            <span>
              <strong>Afecta saldos del wallet</strong>
              <br />
              <span className="text-slate-400">
                Desactiva si es un registro excepcional sin movimiento real
              </span>
            </span>
          </label>

          {form.affects_wallet && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Moneda</label>
              <select
                value={form.currency_type_id}
                onChange={(e) => handleChange('currency_type_id', e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
              >
                {currencyTypes.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                setForm({ ...EMPTY_FORM, entry_date: new Date().toISOString().slice(0, 10) });
                setError('');
                setSuccess('');
              }}
              className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm"
            >
              Limpiar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
            >
              {isSaving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-bold mb-3">
          {activeTab === 'income' ? 'Ingresos Registrados' : 'Egresos Registrados'} ({entries.length})
        </h3>
        {isLoading ? (
          <LoadingState />
        ) : entries.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No hay registros</p>
        ) : (
          <ul className="space-y-2">
            {entries.slice(0, 20).map((entry: any) => (
              <li key={entry.id} className="border-b border-slate-100 pb-2 last:border-0">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {entry.concept_name || 'Sin concepto'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatDate(entry.entry_date)} · {entry.beneficiary_external_name || entry.beneficiary_id || '—'}
                    </p>
                  </div>
                  <span className={`text-sm font-bold ${activeTab === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(entry.amount)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}